/**
 * 任务复杂度分析命令
 */

import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { ComplexityAnalyzer } from '../core/complexity-analyzer';
import { TaskStore } from '../core/task-store';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import { Task } from '../types';

export function createComplexityCommand(): Command {
  const command = new Command('complexity');
  
  command
    .description('Analyze task complexity with enhanced multi-factor assessment')
    .alias('complex');
  
  // 分析单个任务
  command
    .command('analyze <task>')
    .alias('a')
    .description('Analyze complexity of a single task')
    .option('-d, --detailed', 'Show detailed analysis', false)
    .option('-s, --split', 'Show task breakdown suggestions', false)
    .action(withErrorHandling(async (task: string, options) => {
      const analyzer = new ComplexityAnalyzer();
      const analysis = await analyzer.analyzeComplexity(task);
      
      // 显示基础信息
      console.log(colors.header('\n📊 Task Complexity Analysis\n'));
      console.log(colors.info(`Task: ${task}`));
      console.log();
      
      // 复杂度评分
      const scoreColor = analysis.score > 70 ? colors.red :
                        analysis.score > 50 ? colors.yellow :
                        colors.green;
      
      console.log(`Complexity Score: ${scoreColor(`${analysis.score}/100`)}`);
      console.log(`Level: ${getLevelEmoji(analysis.level)} ${colors.bold(analysis.level.toUpperCase())}`);
      console.log(`Estimated Time: ${colors.highlight(`${analysis.estimatedHours} hours`)}`);
      console.log(`Confidence: ${getConfidenceBar(analysis.confidence)} ${(analysis.confidence * 100).toFixed(0)}%`);
      console.log();
      
      // 推荐方法
      console.log(colors.header('📝 Recommended Approach:'));
      console.log(analysis.recommendedApproach);
      console.log();
      
      // 风险提示
      if (analysis.risks.length > 0) {
        console.log(colors.header('⚠️  Risks:'));
        analysis.risks.forEach(risk => {
          console.log(colors.warning(`• ${risk}`));
        });
        console.log();
      }
      
      // 缓解措施
      if (analysis.mitigations.length > 0) {
        console.log(colors.header('✅ Mitigations:'));
        analysis.mitigations.forEach(mitigation => {
          console.log(colors.success(`• ${mitigation}`));
        });
        console.log();
      }
      
      // 详细因素分析
      if (options.detailed) {
        console.log(colors.header('🔍 Detailed Factors:'));
        const factorTable = new Table({
          columns: [
            { name: 'category', title: 'Category', alignment: 'left' },
            { name: 'factor', title: 'Factor', alignment: 'left' },
            { name: 'score', title: 'Score', alignment: 'center' },
            { name: 'bar', title: 'Level', alignment: 'left' }
          ]
        });
        
        // 基础因素
        factorTable.addRow({ 
          category: 'Basic', 
          factor: 'Text Length', 
          score: analysis.factors.textLength,
          bar: getScoreBar(analysis.factors.textLength)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Technical Terms', 
          score: analysis.factors.technicalTerms,
          bar: getScoreBar(analysis.factors.technicalTerms)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Dependencies', 
          score: analysis.factors.dependencies,
          bar: getScoreBar(analysis.factors.dependencies)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Uncertainty', 
          score: analysis.factors.uncertainty,
          bar: getScoreBar(analysis.factors.uncertainty)
        });
        
        // 技术因素
        factorTable.addRow({ 
          category: 'Technical', 
          factor: 'Integration', 
          score: analysis.factors.integration,
          bar: getScoreBar(analysis.factors.integration)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Algorithmic Depth', 
          score: analysis.factors.algorithmicDepth,
          bar: getScoreBar(analysis.factors.algorithmicDepth)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Data Complexity', 
          score: analysis.factors.dataComplexity,
          bar: getScoreBar(analysis.factors.dataComplexity)
        });
        
        // 范围因素
        factorTable.addRow({ 
          category: 'Scope', 
          factor: 'Impact Scope', 
          score: analysis.factors.scope,
          bar: getScoreBar(analysis.factors.scope)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Cross-Functional', 
          score: analysis.factors.crossFunctional,
          bar: getScoreBar(analysis.factors.crossFunctional)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'User Impact', 
          score: analysis.factors.userImpact,
          bar: getScoreBar(analysis.factors.userImpact)
        });
        
        // 风险因素
        factorTable.addRow({ 
          category: 'Risk', 
          factor: 'Security Risk', 
          score: analysis.factors.securityRisk,
          bar: getScoreBar(analysis.factors.securityRisk)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Performance Risk', 
          score: analysis.factors.performanceRisk,
          bar: getScoreBar(analysis.factors.performanceRisk)
        });
        factorTable.addRow({ 
          category: '', 
          factor: 'Compatibility Risk', 
          score: analysis.factors.compatibilityRisk,
          bar: getScoreBar(analysis.factors.compatibilityRisk)
        });
        
        factorTable.printTable();
        console.log();
      }
      
      // 任务拆分建议
      if (options.split || analysis.breakdown) {
        if (analysis.breakdown?.shouldSplit) {
          console.log(colors.header('✂️  Task Breakdown Suggestion:'));
          console.log(colors.warning(analysis.breakdown.reason));
          console.log();
          
          console.log('Suggested Subtasks:');
          const subtaskTable = new Table({
            columns: [
              { name: 'title', title: 'Subtask', alignment: 'left', maxLen: 40 },
              { name: 'hours', title: 'Hours', alignment: 'right' },
              { name: 'complexity', title: 'Complex', alignment: 'center' },
              { name: 'deps', title: 'Dependencies', alignment: 'left', maxLen: 30 }
            ]
          });
          
          analysis.breakdown.suggestedSubtasks.forEach(subtask => {
            subtaskTable.addRow({
              title: subtask.title,
              hours: subtask.estimatedHours.toFixed(1),
              complexity: subtask.complexity,
              deps: subtask.dependencies.join(', ') || '-'
            });
          });
          
          subtaskTable.printTable();
        } else {
          console.log(colors.success('✅ Task size is manageable, no splitting needed'));
        }
      }
    }));
  
  // 批量分析
  command
    .command('batch')
    .alias('b')
    .description('Analyze complexity of multiple tasks')
    .option('-f, --file <file>', 'Read tasks from file')
    .option('-c, --current', 'Analyze current tasks in workflow')
    .option('-o, --output <file>', 'Save report to file')
    .option('--json', 'Output as JSON')
    .action(withErrorHandling(async (options) => {
      const analyzer = new ComplexityAnalyzer();
      let tasks: Array<string | Task> = [];
      
      // 获取任务列表
      if (options.file) {
        const content = await fs.readFile(path.resolve(options.file), 'utf-8');
        tasks = content.split('\n').filter(line => line.trim());
      } else if (options.current) {
        const taskStore = new TaskStore();
        const allTasks = await taskStore.getTasks();
        tasks = allTasks.filter(t => t.status !== 'completed');
      } else {
        // 交互式输入
        const { default: inquirer } = await import('inquirer');
        const { taskList } = await inquirer.prompt([{
          type: 'editor',
          name: 'taskList',
          message: 'Enter tasks (one per line):'
        }]);
        tasks = taskList.split('\n').filter((line: string) => line.trim());
      }
      
      if (tasks.length === 0) {
        console.log(colors.warning('No tasks to analyze'));
        return;
      }
      
      // 分析任务
      console.log(colors.info(`\nAnalyzing ${tasks.length} tasks...\n`));
      const analyses = await analyzer.batchAnalyze(tasks);
      
      // JSON 输出
      if (options.json) {
        const output = {
          timestamp: new Date().toISOString(),
          totalTasks: tasks.length,
          analyses: analyses.map((analysis, index) => ({
            task: typeof tasks[index] === 'string' ? tasks[index] : tasks[index].content,
            ...analysis
          }))
        };
        
        if (options.output) {
          await fs.writeJson(path.resolve(options.output), output, { spaces: 2 });
          console.log(colors.success(`Report saved to: ${options.output}`));
        } else {
          console.log(JSON.stringify(output, null, 2));
        }
        return;
      }
      
      // 表格输出
      const table = new Table({
        columns: [
          { name: 'task', title: 'Task', alignment: 'left', maxLen: 40 },
          { name: 'score', title: 'Score', alignment: 'center' },
          { name: 'level', title: 'Level', alignment: 'center' },
          { name: 'hours', title: 'Hours', alignment: 'right' },
          { name: 'split', title: 'Split?', alignment: 'center' },
          { name: 'risks', title: 'Risks', alignment: 'center' }
        ]
      });
      
      analyses.forEach((analysis, index) => {
        const taskContent = typeof tasks[index] === 'string' 
          ? tasks[index] 
          : (tasks[index] as Task).content;
        
        table.addRow({
          task: taskContent.length > 40 ? taskContent.substring(0, 37) + '...' : taskContent,
          score: getScoreColor(analysis.score)(`${analysis.score}`),
          level: getLevelEmoji(analysis.level),
          hours: analysis.estimatedHours.toFixed(1),
          split: analysis.breakdown?.shouldSplit ? '✂️' : '-',
          risks: analysis.risks.length > 0 ? `⚠️ ${analysis.risks.length}` : '✅'
        });
      });
      
      table.printTable();
      console.log();
      
      // 生成报告
      const report = analyzer.generateReport(analyses);
      console.log(report);
      
      // 保存报告
      if (options.output) {
        await fs.writeFile(path.resolve(options.output), report);
        console.log(colors.success(`\nReport saved to: ${options.output}`));
      }
    }));
  
  // 配置复杂度阈值
  command
    .command('config')
    .description('Configure complexity thresholds')
    .option('--split-threshold <hours>', 'Set task split threshold (hours)', '8')
    .option('--complexity-threshold <score>', 'Set complexity warning threshold', '70')
    .action(withErrorHandling(async (options) => {
      const configPath = path.join(process.cwd(), '.workflow/complexity-config.json');
      
      const config = {
        splitThreshold: parseFloat(options.splitThreshold),
        complexityThreshold: parseInt(options.complexityThreshold),
        updatedAt: new Date().toISOString()
      };
      
      await fs.ensureDir(path.dirname(configPath));
      await fs.writeJson(configPath, config, { spaces: 2 });
      
      console.log(colors.success('✅ Complexity configuration updated'));
      console.log(`Split threshold: ${config.splitThreshold} hours`);
      console.log(`Complexity warning: ${config.complexityThreshold}/100`);
    }));
  
  return command;
}

// 辅助函数
function getLevelEmoji(level: string): string {
  const emojis = {
    'trivial': '🟢',
    'simple': '🟡',
    'medium': '🟠',
    'complex': '🔴',
    'very-complex': '🟣',
    'extreme': '⚫'
  };
  return emojis[level as keyof typeof emojis] || '⚪';
}

function getScoreBar(score: number): string {
  const filled = Math.round(score / 2);
  const empty = 5 - filled;
  return '█'.repeat(filled) + '░'.repeat(empty);
}

function getScoreColor(score: number) {
  if (score > 70) return colors.red;
  if (score > 50) return colors.yellow;
  return colors.green;
}

function getConfidenceBar(confidence: number): string {
  const percentage = Math.round(confidence * 100);
  const filled = Math.round(percentage / 10);
  const empty = 10 - filled;
  return '▰'.repeat(filled) + '▱'.repeat(empty);
}